import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { CreditCard, Users, Layers, Repeat } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';

interface CommunityStats {
  totalCards: number;
  totalDecks: number;
  totalUsers: number;
  totalTrades: number;
}

export function CommunityStats() {
  const [stats, setStats] = useState<CommunityStats>({
    totalCards: 0,
    totalDecks: 0,
    totalUsers: 0,
    totalTrades: 0
  });
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchStats = async () => {
      try {
        // Récupérer le nombre total de cartes
        const { count: totalCards } = await supabase
          .from('cards')
          .select('*', { count: 'exact', head: true });

        // Récupérer le nombre total de decks publics
        const { count: totalDecks } = await supabase
          .from('decks')
          .select('*', { count: 'exact', head: true })
          .eq('is_public', true);

        // Récupérer le nombre total d'utilisateurs actifs (avec au moins une carte ou un deck)
        const { count: totalUsers } = await supabase
          .from('profiles')
          .select('*', { count: 'exact', head: true });

        // Récupérer le nombre total d'échanges
        const { count: totalTrades } = await supabase
          .from('trades')
          .select('*', { count: 'exact', head: true });

        setStats({
          totalCards: totalCards || 0,
          totalDecks: totalDecks || 0,
          totalUsers: totalUsers || 0,
          totalTrades: totalTrades || 0
        });
      } catch (error) {
        console.error('Erreur lors de la récupération des statistiques:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const formatNumber = (num: number) => {
    if (num >= 1000) {
      return `${Math.floor(num/1000)}k+`;
    }
    return `${num}`;
  };

  const statsData = [
    {
      value: loading ? '0' : formatNumber(stats.totalCards),
      label: t('landing.features.stat1_label'),
      icon: <CreditCard className="w-8 h-8 text-white mb-2" />,
    },
    {
      value: loading ? '0' : formatNumber(stats.totalDecks),
      label: t('landing.features.stat2_label'),
      icon: <Layers className="w-8 h-8 text-white mb-2" />,
    },
    {
      value: loading ? '0' : formatNumber(stats.totalUsers),
      label: t('landing.features.stat3_label'),
      icon: <Users className="w-8 h-8 text-white mb-2" />,
    },
    {
      value: loading ? '0' : formatNumber(stats.totalTrades),
      label: t('landing.features.stat4_label'),
      icon: <Repeat className="w-8 h-8 text-white mb-2" />,
    },
  ];

  if (loading) {
    return (
      <div className="bg-primary-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-primary-500 rounded w-3/4 mx-auto mb-4"></div>
              <div className="h-4 bg-primary-500 rounded w-1/2 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-primary-600">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-white mb-3">
            {t('landing.features.community_title')}
          </h2>
          <p className="text-primary-100 text-lg">
            {t('landing.features.community_description')}
          </p>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {statsData.map((stat) => (
            <div key={stat.label} className="text-center">
              {stat.icon}
              <div className="text-4xl font-bold text-white mb-2">
                {stat.value}
              </div>
              <div className="text-primary-100">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}